// ─── Permission Utilities ──────────────────────────────────────────────
//
// These utilities handle client-side authorization checks.
// IMPORTANT: Client-side checks are for UX only - the server always
// has final authority on authorization decisions.

import type { PermissionName } from '@/features/auth/constants/permissions';
import type { RoleName } from '@/features/auth/constants/roles';

// ─── Wildcard Matching ────────────────────────────────────────────────

/**
 * Check if a user permission matches a required permission.
 * Supports wildcards:
 *   - Exact match: 'expense.create' === 'expense.create'
 *   - Resource wildcard: 'expense.*' matches 'expense.create', 'expense.read', etc.
 *   - All wildcard: '*' or '*.*' matches any permission
 */
export function matchPermission(
  requiredPermission: string,
  userPermission: string,
): boolean {
  // Exact match
  if (userPermission === requiredPermission) {
    return true;
  }

  // All wildcard
  if (userPermission === '*' || userPermission === '*.*') {
    return true;
  }

  const reqParts = requiredPermission.split('.');
  const reqResource = reqParts[0] ?? '';

  const userParts = userPermission.split('.');
  const userResource = userParts[0] ?? '';
  const userAction = userParts[1] ?? '';

  // Resource wildcard: expense.* matches expense.create
  if (userAction === '*' && userResource === reqResource) {
    return true;
  }

  // Resource shorthand: 'expense' matches expense.*
  if (userAction === '' && userResource === reqResource) {
    return true;
  }

  // Group permission: expense.manage should match? 
  // No — group permissions are backend-defined mappings,
  // not wildcards. They're matched exactly like regular permissions.
  // e.g., if a user has 'expense.manage', they have that literal permission.

  return false;
}

/**
 * Check if the user has a specific permission in their permission set.
 * Supports wildcard matching against all user permissions.
 */
export function can(
  requiredPermission: PermissionName | string,
  userPermissions: string[],
): boolean {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  return userPermissions.some((userPerm) =>
    matchPermission(requiredPermission, userPerm),
  );
}

/**
 * Inverse of can(). Returns true if the user does NOT have the permission.
 */
export function cannot(
  requiredPermission: PermissionName | string,
  userPermissions: string[],
): boolean {
  return !can(requiredPermission, userPermissions);
}

/**
 * Check if the user has ALL of the specified permissions.
 */
export function hasAllPermissions(
  requiredPermissions: (PermissionName | string)[],
  userPermissions: string[],
): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  return requiredPermissions.every((perm) => can(perm, userPermissions));
}

/**
 * Check if the user has ANY of the specified permissions.
 */
export function hasAnyPermission(
  requiredPermissions: (PermissionName | string)[],
  userPermissions: string[],
): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  return requiredPermissions.some((perm) => can(perm, userPermissions));
}

// ─── Role Functions ───────────────────────────────────────────────────

/**
 * Check if the user has a specific role.
 */
export function hasRole(
  requiredRole: RoleName | string,
  userRoles: string[],
): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return userRoles.includes(requiredRole);
}

/**
 * Check if the user has ANY of the specified roles.
 */
export function hasAnyRole(
  requiredRoles: (RoleName | string)[],
  userRoles: string[],
): boolean {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  return requiredRoles.some((role) => hasRole(role, userRoles));
}

/**
 * Check if the user has ALL of the specified roles.
 */
export function hasAllRoles(
  requiredRoles: (RoleName | string)[],
  userRoles: string[],
): boolean {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  return requiredRoles.every((role) => hasRole(role, userRoles));
}

/**
 * Check if the user is a Super Admin.
 */
export function isSuperAdmin(userRoles: string[]): boolean {
  return userRoles.includes('super_admin');
}
